import React from 'react';
import { useFloatingInputLogic } from './floating-input.hooks';
import {
  Group,
  Prefix,
  Input,
  Label,
  Toggle,
  EditIcon,
  ErrorText,
  EyeVisible,
  EyeInvisible,
} from './floating-input.styled';
import type { FloatingInputProps } from './floating-input.types';

export const FloatingInput: React.FC<FloatingInputProps> = ({
  id,
  label = 'Input',
  value,
  onChange,

  /* html/aria defaults */
  type = 'text',
  placeholder = ' ',
  required = false,
  disabled = false,
  readOnly = false,
  minLength,
  maxLength,
  pattern,
  title,
  name,
  autoComplete,
  autoFocus = false,
  inputMode,
  spellCheck = false,
  onBlur,
  onFocus,
  onKeyDown,
  testid,
  /* extras */
  numericOnly = false,
  showToggle,
  prefix,
  prefixWidth = 64,
  /* error */
  errorText,
  editText,
}) => {
  const { isPasswordField, revealed, setRevealed, effectiveType, handleKeyDown, shouldShowToggle } =
    useFloatingInputLogic({ type, numericOnly, showToggle, onKeyDown });

  const hasError = Boolean(errorText);
  const hasPrefix = Boolean(prefix);
  const hasEditText = Boolean(editText);

  return (
    <>
      <Group>
        {hasPrefix && <Prefix style={{ width: prefixWidth }}>{prefix}</Prefix>}

        <Input
          data-testid={testid}
          id={id}
          type={numericOnly ? 'text' : effectiveType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          minLength={minLength}
          maxLength={maxLength}
          pattern={numericOnly ? '\\d*' : pattern}
          title={title}
          name={name}
          autoComplete={
            autoComplete ??
            (numericOnly ? 'one-time-code' : isPasswordField ? 'new-password' : 'on')
          }
          autoFocus={autoFocus}
          inputMode={numericOnly ? 'numeric' : inputMode}
          spellCheck={spellCheck}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          hasToggle={shouldShowToggle}
          hasPrefix={hasPrefix}
          prefixWidth={prefixWidth}
          hasError={hasError}
          hasEditText={hasEditText}
        />

        <Label htmlFor={id} hasPrefix={hasPrefix} prefixWidth={prefixWidth}>
          {label}
        </Label>

        {shouldShowToggle && (
          <Toggle
            type="button"
            aria-label={revealed ? 'Hide password' : 'Show password'}
            onClick={() => setRevealed((s) => !s)}
          >
            {revealed ? (
              <EyeVisible key={'visible'} src="/visible.svg" alt="visible" />
            ) : (
              <EyeInvisible key={'invisible'} src="/invisible.svg" alt="invisible" />
            )}
          </Toggle>
        )}

        {hasEditText && <EditIcon src="/edit-email.svg" alt="Edit input" />}
      </Group>
      {hasError && <ErrorText>{errorText}</ErrorText>}
    </>
  );
};
